import React from 'react';
import { UserKind } from 'src/components/BasketShared/ShareBasketViaEmailForm';
import cx from 'classnames';
import { getCopyParams } from 'src/components/Basket/utils/getCopyTargets';
import { CustomisationWithIdAndLocation } from 'src/components/CustomisationPage/feature/customisation.types';

import { Price } from '../Helpers/Price';

import { BasketItemType } from './BasketContext';
import { BasketItem } from './BasketItem';
import classes from './BasketItems.scss';

export const canCopyCustomisation = (customisationId: number, itemId: string, items: BasketItemType[]) => {
    if (items.length <= 1) return false;

    if (!items.find((item) => item.customisations.length > 0)) return false;

    const itemsWithoutSelected = items.filter((item) => item.cartItem.id !== itemId);

    const currentItem = items.find((item) => item.cartItem.id === itemId);
    const currentCustomisation = currentItem?.customisations.find(
        (customisation) => customisation.id === customisationId,
    );

    if (currentCustomisation?.customisation.is_free) return false;

    const itemsWithThisCustomisation = itemsWithoutSelected.filter((item) => {
        const isTheSameLocation = item.customisations.find(
            (item) => item.location_name === currentCustomisation?.location_name,
        );

        if (isTheSameLocation) return isTheSameLocation;
        return item.customisations.some((customisation) => customisation.id === customisationId);
    });

    return Boolean(itemsWithThisCustomisation.length - itemsWithoutSelected.length);
};

const getValidAndInValidCustomisations = (
    itemId: string,
    customisation: CustomisationWithIdAndLocation,
    items: BasketItemType[],
) => getCopyParams(items, itemId, customisation);

type Props = {
    isPending: boolean;
    items: BasketItemType[];
    itemsPending: string[];
    userKind: UserKind;
    setupCost: number;
    setupCostAfterDiscount: number;
    setupCostDiscount: number;
};

export const BasketItems: React.FC<Props> = React.memo(
    ({ items, isPending, itemsPending, setupCost, setupCostAfterDiscount, setupCostDiscount, userKind }) => (
        <>
            <table className={classes.basketItems} data-loading={isPending}>
                <tbody>
                    {items.map((item) => {
                        const { cartItem } = item;
                        const pending = itemsPending.indexOf(cartItem.id) > -1;
                        return (
                            <BasketItem
                                item={item}
                                isPending={pending}
                                key={cartItem.id!}
                                userKind={userKind}
                                canCopyCustomisation={(customisationId, itemId) =>
                                    canCopyCustomisation(customisationId, itemId, items)
                                }
                                getCopyParams={(itemId, customisation) =>
                                    getValidAndInValidCustomisations(itemId, customisation, items)
                                }
                            />
                        );
                    })}
                </tbody>
            </table>

            {(setupCostAfterDiscount > 0 || setupCostDiscount > 0) && (
                <div className={classes.basketItemsSetupCostWrapper}>
                    <div
                        className={cx(classes.basketItemsSetupCost, {
                            [classes.basketItemsSetupCost__longer]: Boolean(setupCostDiscount),
                        })}
                    >
                        <p className={classes.basketItemsSetupCost__title}>All logo setup costs:</p>
                        {Boolean(setupCostDiscount) && (
                            <span className={classes.beforeDiscount}>
                                <Price price={setupCost} priceIncl={setupCost} />
                            </span>
                        )}
                        <p className={classes.basketItemsSetupCost__cost}>
                            <Price price={setupCostAfterDiscount || 0} priceIncl={setupCostAfterDiscount || 0} />
                        </p>
                    </div>
                </div>
            )}
        </>
    ),
);
